import React, { useState } from "react";
import { useMutation, useQuery } from "react-query";
import { DEFAULT_PER_PAGE } from "../../../helpers/default";
import ContentBlock from "../../../components/Layout/ContentBlock";
import FilterMenu from "../../../components/Layout/FilterMenu";
import {
    ButtonSecondary,
    Pagination,
    TextInput,
} from "../../../components/globals";
import TableFilterBox from "../../../components/common/TableFilterBox";
import {
    ButtonSmall
} from "../../../components/common/Buttons";
import {
    antdAsyncMessageError,
    antdAsyncMessageSuccess,
} from "../../../helpers/AsyncMessage";
import { Modal, DatePicker } from "antd";
import { useModal } from "../../../hooks";
import axios from "axios";
import ContentValidation from "../../../components/common/ContentValidation";
import TabUi from "./TabUi";
import { IconSearch } from "../../../components/Layout/Icons";
import { IconVVStockDocument } from "../../../components/Layout/Icons";
import ShipmentComponentIndex from "../../../components/orders/accepted_orders/items/ShipmentComponentIndex";
import ShipmentStoreSvg from "../../../../assets/images/shipment/shipment_store.svg";
import DocumentDetail from "./DocumentDetail";

const { confirm } = Modal;

const NewDocuments = ({ tabId, setTabId }) => {
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(DEFAULT_PER_PAGE);
    const [totals, setTotals] = useState(0);
    const [selectedDocumentId, setSelectedDocumentId] = useState(null);
    const [visibleShipmentId, setVisibleShipmentId] = useState(null);

    // Filter states
    const [filterDocumentDate, setFilterDocumentDate] = useState(null);
    const [filterProcessingDate, setFilterProcessingDate] = useState(null);
    const [filterSender, setFilterSender] = useState("");
    const [filterReceiver, setFilterReceiver] = useState("");
    const [filterManager, setFilterManager] = useState("");
    const [filterDelivery, setFilterDelivery] = useState("");

    const { modalOpt, setModalOpt, closeModal } = useModal();

    // Wrapper to render the full shipment UI for a document row
    const WrapperShipmentForDocument = ({ doc, onClose }) => {
        const orderRows = (doc?.product_transfer_items || []).map((item, index) => ({
            id: item.id,
            order_id: doc?.id,
            selected: true,
            quantity: item.quantity,
            product_name: item.product?.title || "Unknown Product",
            product_reference: item.product?.sku || "",
            unit_price_tax_incl: item.prime_cost || 0,
            shipment_order_rows: [],
        }));

        const fakeOrderRows = orderRows.length > 0 ? orderRows : [
            {
                id: 1,
                order_id: doc?.id,
                selected: true,
                quantity: 1,
                product_name: doc?.document_number || "Document item",
                product_reference: "",
                unit_price_tax_incl: 0,
                shipment_order_rows: [],
            },
        ];

        // Receiver address for shipping
        const shipping_address = {
            firstname: doc?.receiver?.name || doc?.receiver?.title || "Receiver",
            lastname: "",
            postal_code: doc?.receiver?.postal_code || "00000",
            street: doc?.receiver?.address || "Address not provided",
            city: doc?.receiver?.city || "Vilnius",
            phone: doc?.receiver?.phone || "+37000000000",
            other: doc?.receiver?.email || "",
            comment: doc?.receiver?.comment || "",
            country_id: doc?.receiver?.country_id || doc?.country_id || 122,
            country: doc?.receiver?.country || doc?.country || "LT",
            parcel_terminal_id: null,
            name: doc?.receiver?.name || doc?.receiver?.title || "Receiver",
            address: doc?.receiver?.address || "Address not provided",
            email: doc?.receiver?.email || "",
        };
        // Sender pickup address
        const carrier = {
            module_name: doc?.delivery || "dpd",
            name: doc?.delivery || "dpd",
            pickup_address_id: doc?.sender?.pickup_address_id || null
        };

        const handleShipmentCreated = () => {
            onClose();
            refetchDocuments();
        };

        return (
            <ShipmentComponentIndex
                order_rows={fakeOrderRows}
                id={doc?.id}
                id_order={doc?.id}
                remark={""}
                shipping_address={shipping_address}
                total_price={0}
                payment_module={"cash"}
                carrier={carrier}
                pickup_address_id={doc?.sender?.pickup_address_id || null}
                onClose={onClose}
                order_invoices={[]}
                order_cart_rules={[]}
                billing_address={{}}
                shipments={doc?.shipments || []}
                assigned_bills={[]}
                cod={false}
                leasing_cod={false}
                leasing_advance={0}
                waiting_payments={[]}
                order_shop_id={0}
                order_shop={{ identifier: "VV" }}
                form_type={"ProductTransfer"}
                total_shipping_tax_incl={0}
                callBackOnCreate={handleShipmentCreated}
            />
        );
    };

    const handleOpenShipment = (rowId) => {
        if (visibleShipmentId === rowId) {
            setVisibleShipmentId(null);
            return;
        }
        setVisibleShipmentId(rowId);
        const anchor = document.querySelector(`.shipmentIndex_${rowId}`);
        if (anchor) {
            anchor.scrollIntoView({ block: "center", behavior: "smooth" });
        }
    };

    const handleKeyDownOpenShipment = (event, rowId) => {
        if (!event) return;
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            handleOpenShipment(rowId);
        }
    };

    const fetchDocuments = async () => {
        let url = `/api/v1/product_transfers?page=${page}&per_page=${perPage}`;

        const queryParams = {
            'q[status_eq]': 'new'
        };

        if (filterDocumentDate) {
            const dateStr = filterDocumentDate.format('YYYY-MM-DD');
            queryParams['q[created_at_gteq]'] = `${dateStr} 00:00:00`;
            queryParams['q[created_at_lteq]'] = `${dateStr} 23:59:59`;
        }
        if (filterProcessingDate) {
            const dateStr = filterProcessingDate.format('YYYY-MM-DD');
            queryParams['q[updated_at_gteq]'] = `${dateStr} 00:00:00`;
            queryParams['q[updated_at_lteq]'] = `${dateStr} 23:59:59`;
        }
        if (filterSender) {
            queryParams['q[sender_name_or_sender_title_cont]'] = filterSender;
        }
        if (filterReceiver) {
            queryParams['q[receiver_name_or_receiver_title_cont]'] = filterReceiver;
        }
        if (filterManager) {
            queryParams['q[manager_full_name_cont]'] = filterManager;
        }
        if (filterDelivery) {
            queryParams['q[delivery_cont]'] = filterDelivery;
        }

        // Add query parameters to URL
        const queryString = new URLSearchParams(queryParams).toString();
        if (queryString) {
            url += `&${queryString}`;
        }

        try {
            const response = await axios.get(url);
            return response.data;
        } catch (error) {
            console.error('Error fetching documents:', error);
            // Return empty data on error
            return {
                data: [],
                total: 0,
                totals: 0
            };
        }
    };

    const {
        data: documents,
        isLoading: documentsLoading,
        error: documentsError,
        refetch: refetchDocuments,
    } = useQuery(["vv_stock_new_documents", page, perPage], fetchDocuments, {
        enabled: false
    });

    const handleFilterChange = () => {
        refetchDocuments();
    };

    React.useEffect(() => {
        refetchDocuments();
    }, [page, perPage]);

    const handleFilterClear = () => {
        setFilterDocumentDate(null);
        setFilterProcessingDate(null);
        setFilterSender("");
        setFilterReceiver("");
        setFilterManager("");
        setFilterDelivery("");
    };

    const deleteDocumentMutation = useMutation(
        (id) => axios.delete(`/api/v1/product_transfers/${id}`),
        {
            onSuccess: () => {
                antdAsyncMessageSuccess({
                    text: "Successfully deleted",
                    key: "delete_document",
                });
                refetchDocuments();
            },
            onError: () => {
                antdAsyncMessageError({
                    text: "Error occurred",
                    key: "delete_document",
                });
            },
        }
    );

    React.useEffect(() => {
        setTotals(documents?.total || 0);
    }, [documents]);

    React.useEffect(() => {
        refetchDocuments();
    }, [modalOpt]);

    // Show detail view if a document is selected
    if (selectedDocumentId) {
        return (
            <DocumentDetail
                documentId={selectedDocumentId}
                onBack={() => setSelectedDocumentId(null)}
                onRefresh={() => refetchDocuments()}
            />
        );
    }

    return (
        <>
            <ContentBlock>
                <FilterMenu
                    length={documents?.data?.length}
                    text2={`products`}
                    iconComponent={<IconVVStockDocument />}
                    onChangeCountPage={setPerPage}
                    countPage={perPage}
                    page={page}
                    totals={documents?.totals}
                />
            </ContentBlock>

            <div className="col-12">
                <TabUi tabId={tabId} setTabId={setTabId} />
            </div>

            <ContentBlock>
                <TableFilterBox
                    filter={true}
                    onFilter={() => {
                        handleFilterChange();
                    }}
                    add={false}
                    clearFilter={() => handleFilterClear()}
                    button1={
                        <>
                            <div className="float-left ml-2" style={{ width: 104 }}>
                                <ButtonSecondary
                                    onClick={() => setModalOpt("new_document", 0)}
                                >
                                    New Document
                                </ButtonSecondary>
                            </div>
                        </>
                    }
                >
                    <div className="d-flex justify-content-between flex-1 flex-wrap">
                        <div className="d-flex flex-wrap">
                            <div className="px-1">
                                <div className="form-group">
                                    <label className="text-sm-label my-1">Document date</label>
                                    <DatePicker
                                        style={{ width: '100%' }}
                                        value={filterDocumentDate}
                                        onChange={(date) => {
                                            setFilterDocumentDate(date);
                                        }}
                                        format="YYYY-MM-DD"
                                        allowClear
                                        placeholder="Select date"
                                    />
                                </div>
                            </div>
                            <div className="px-1">
                                <div className="form-group">
                                    <label className="text-sm-label my-1">Processing date</label>
                                    <DatePicker
                                        style={{ width: '100%' }}
                                        value={filterProcessingDate}
                                        onChange={(date) => {
                                            setFilterProcessingDate(date);
                                        }}
                                        format="YYYY-MM-DD"
                                        allowClear
                                        placeholder="Select date"
                                    />
                                </div>
                            </div>
                            <div className="px-1">
                                <TextInput
                                    text="Sender"
                                    name="sender"
                                    value={filterSender}
                                    onChange={(val) => {
                                        setFilterSender(val.target.value);
                                    }}
                                />
                            </div>
                            <div className="px-1">
                                <TextInput
                                    text="Receiver"
                                    name="receiver"
                                    value={filterReceiver}
                                    onChange={(val) => {
                                        setFilterReceiver(val.target.value);
                                    }}
                                />
                            </div>
                            <div className="px-1">
                                <TextInput
                                    text="Manager"
                                    name="manager"
                                    value={filterManager}
                                    onChange={(val) => {
                                        setFilterManager(val.target.value);
                                    }}
                                />
                            </div>
                            <div className="px-1">
                                <TextInput
                                    text="Delivery"
                                    name="delivery"
                                    value={filterDelivery}
                                    onChange={(val) => {
                                        setFilterDelivery(val.target.value);
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                </TableFilterBox>

                <div>
                    <div className="table-responsive" style={{ overflow: "hidden" }}>
                        <ContentValidation
                            loader={documentsLoading}
                            array={documents?.data}
                            error={documentsError}
                        >
                            <table className="table mt-3">
                                <thead>
                                    <tr className="text-muted">
                                        <th className="text-black">Document no.</th>
                                        <th className="text-black">Creation date</th>
                                        <th className="text-black">Status</th>
                                        <th className="text-black">Supplier code</th>
                                        <th className="text-black">Manager</th>
                                        <th className="text-black">Sup. order No.</th>
                                        <th className="text-black">Sender</th>
                                        <th className="text-black">Receiver</th>
                                        <th className="text-black">Arrival date</th>
                                        <th className="text-black">Delivery</th>
                                        <th className="text-black">Shipment</th>
                                        <th className="text-black">Processing dates</th>
                                        <th className="text-black">Product QTY</th>
                                        <th className="text-black"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {documents?.data?.map?.((item) => (
                                        <tr className="align-middle tableColumn" key={item.id}>
                                            <td className="align-middle">{item?.document_no}</td>
                                            <td className="align-middle">
                                                {item?.creation_date ? new Date(item.creation_date).toLocaleString('lt-LT', {
                                                    year: 'numeric',
                                                    month: '2-digit',
                                                    day: '2-digit',
                                                    hour: '2-digit',
                                                    minute: '2-digit'
                                                }).replace(',', '') : '-'}
                                            </td>
                                            <td className="align-middle">
                                                <div>
                                                    <div className="d-flex align-items-center">
                                                        <span className="text-info mr-2" style={{ fontSize: "14px" }}>
                                                            ●
                                                        </span>
                                                        {item?.status}
                                                    </div>
                                                    {item?.status === "failed" && item?.error && (
                                                        <div className="text-danger" style={{ fontSize: "11px", marginTop: "4px", marginLeft: "18px" }}>
                                                            {item.error}
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="align-middle">{item?.supplier_code}</td>
                                            <td className="align-middle">{item?.manager}</td>
                                            <td className="align-middle">{item?.sup_order_no}</td>
                                            <td className="align-middle">{item?.sender?.title || item?.sender?.code || item?.sender || "-"}</td>
                                            <td className="align-middle">{item?.receiver?.title || item?.receiver?.code || item?.receiver || "-"}</td>
                                            <td className="align-middle">{item?.arrival_date}</td>
                                            <td className="align-middle">{item?.delivery}</td>
                                            <td className="align-middle">
                                                <div className={`shipmentIndex_${item.id}`}></div>

                                                {item?.shipments && item.shipments.length > 0 && (() => {
                                                    const latestShipment = item.shipments[item.shipments.length - 1];
                                                    return (
                                                        <div className="mb-2">
                                                            {latestShipment.api_errors && (
                                                                <div className="text-danger mb-1" style={{ fontSize: "11px" }}>
                                                                    Error: {latestShipment.api_errors}
                                                                </div>
                                                            )}

                                                            <div style={{ fontSize: "12px" }}>
                                                                <strong>{latestShipment.shipment_type.toUpperCase()}</strong>
                                                                {latestShipment.tracking_numbers && latestShipment.tracking_numbers.length > 0 ? (
                                                                    <div className="mt-1">
                                                                        {latestShipment.tracking_numbers.map((trackingNum, idx) => (
                                                                            <div key={idx} className="text-primary">
                                                                                📦 {trackingNum}
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                ) : latestShipment.shipment_type?.toLowerCase() === 'vv_transport' && !latestShipment.api_errors ? (
                                                                    <div className="mt-1 text-success">
                                                                        Transported with VV Transport.
                                                                    </div>
                                                                ) : null}
                                                            </div>
                                                        </div>
                                                    );
                                                })()}

                                                {visibleShipmentId === item.id ? (
                                                    <div>
                                                        <div className="mb-2 d-flex justify-content-end">
                                                            <ButtonSmall onClick={() => setVisibleShipmentId(null)}>Close</ButtonSmall>
                                                        </div>
                                                        <WrapperShipmentForDocument doc={item} onClose={() => setVisibleShipmentId(null)} />
                                                    </div>
                                                ) : (
                                                    <div
                                                        className="btn-primary d-flex justify-content-center align-items-center cursor-pointer"
                                                        style={{ width: 30, height: 30, borderRadius: "50%" }}
                                                        onClick={() => handleOpenShipment(item.id)}
                                                        aria-label="Open shipment services"
                                                        role="button"
                                                        tabIndex={0}
                                                        onKeyDown={(e) => handleKeyDownOpenShipment(e, item.id)}
                                                    >
                                                        <img src={ShipmentStoreSvg} style={{ width: 18, height: 18, marginLeft: 2 }} />
                                                    </div>
                                                )}
                                            </td>
                                            <td className="align-middle">{item?.processing_dates}</td>
                                            <td className="align-middle">{item?.product_qty}</td>
                                            <td className="align-middle">
                                                <div className="d-flex justify-content-end gap-2">
                                                    <ButtonSmall
                                                        className="d-flex justify-content-center align-items-center"
                                                        onClick={() => {
                                                            setSelectedDocumentId(item.id);
                                                        }}
                                                    >
                                                        <IconSearch color="#fff" />
                                                    </ButtonSmall>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </ContentValidation>
                        {totals > 0 && (
                            <Pagination
                                setPagination={setPage}
                                page={page}
                                totals={totals}
                                pageCount={perPage}
                            />
                        )}
                    </div>
                </div>
            </ContentBlock>

            {/* NOTE: ShipmentAdditionalServices is rendered inline per row; modal removed as requested */}
        </>
    );
};

export default NewDocuments; 